import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, Location } from 'react-router-dom';
import { NavigationSidebar } from '../NavigationSidebar';
import { t } from '../../services/LocalizationService';
import {
  MainLayout,
  TopBar,
  Breadcrumbs,
  BreadcrumbItem,
  UserMenu,
  LanguageSelector,
  Language,
  ToastContainer,
} from '@emfit/shared-ui';
import { SessionStore } from '../../stores';
import { EmfitStore } from '../../stores/EmfitStore';
import { GroupDashboardStore } from '../../stores/GroupDashboardStore';
import { LocalizationService } from '../../services/LocalizationService';
import { Locales, SUPPORTED_LANGUAGES } from '../../config';
import { useNavigate } from 'react-router-dom';
import emfitCareLogo from '../../assets/emfit_care.png';
import { StatusAnnouncer } from '../StatusAnnouncer/StatusAnnouncer';
import { FieldLabelsStore } from '../../viewSettings/fieldLabels';

function getCrumbs(
  location: Location,
  paths: Record<string, string>
): BreadcrumbItem[] {
  const path = location.pathname.split('/').filter(Boolean);

  const crumbs = path.map((_, i) => {
    const parts = path.slice(0, i + 1);
    const current = parts[parts.length - 1];
    const name = paths[current] || current;
    const url = '/' + parts.join('/');

    return {
      label: name,
      href: url,
    };
  });

  return crumbs;
}

const languages: Language[] = SUPPORTED_LANGUAGES;

export const Main = observer(() => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    EmfitStore.getAllDevices();
    GroupDashboardStore.setMasterUser(SessionStore.isMasterUser);
    void GroupDashboardStore.fetchGroupDashboard();
  }, []);

  useEffect(() => {
    void FieldLabelsStore.load();
  }, []);

  // Computed inside observer so it re-evaluates when locale changes
  const paths: Record<string, string> = {
    devices: t('Devices'),
    dashboard: t('Group dashboard'),
    monitor: t('Monitor'),
    // The /trends route renders the Timelines page.
    trends: t('Timelines'),
    users: t('Users & Groups'),
    'audit-log': t('Audit Log'),
    settings: t('Profile'),
    overview: t('Timelines'),
    events: t('Events'),
    'notification-settings': t('Notification Settings'),
    'device-settings': t('Device Settings'),
  };

  // Resolve :deviceId URL segment to device name for breadcrumb
  const deviceIdMatch = location.pathname.match(/^\/devices\/(.+)/);
  const rawDeviceId = deviceIdMatch?.[1];
  const resolvedDeviceName: string | undefined = rawDeviceId
    ? EmfitStore.getDeviceBySerial(rawDeviceId)?.deviceName ?? rawDeviceId
    : undefined;

  const pathsWithDevice =
    resolvedDeviceName && rawDeviceId
      ? { ...paths, [rawDeviceId]: resolvedDeviceName }
      : paths;

  const crumbs = getCrumbs(location, pathsWithDevice);
  const locationText = crumbs[crumbs.length - 1]?.label || t('Home');

  const handleLogout = () => {
    void SessionStore.logout().then(() => navigate('/login'));
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleLanguageChange = (languageCode: string) => {
    LocalizationService.locale = languageCode as Locales;
  };

  // Mobile logo
  const logo = (
    <Link to="/" aria-label="Emfit Care">
      <img src={emfitCareLogo} alt="Emfit Care" className="h-6 dark:invert" />
    </Link>
  );

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-foreground focus:rounded focus:shadow-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <StatusAnnouncer />
      <ToastContainer />
      <MainLayout
        sidebar={
          <NavigationSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        }
        topBar={
          <TopBar
            breadcrumbs={<Breadcrumbs items={crumbs} />}
            logo={logo}
            title={locationText}
            languageSelector={
              <LanguageSelector
                languages={languages}
                currentLanguage={LocalizationService.locale}
                onLanguageChange={handleLanguageChange}
              />
            }
            userMenu={
              <UserMenu
                username={SessionStore.userData?.name}
                onSettingsClick={handleSettings}
                onLogoutClick={handleLogout}
                t={t}
              />
            }
            onMenuClick={() => setSidebarOpen(true)}
          />
        }
      >
        <main id="main-content" className="flex-1 min-h-0 flex flex-col">
          <Outlet />
        </main>
      </MainLayout>
    </>
  );
});
